import React, { useState, useEffect, useCallback } from 'react';
import { getExports, deleteExport, createExport, getQueryHistory } from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Download, 
  Trash2, 
  FileSpreadsheet,
  FileText,
  FileJson,
  Clock,
  HardDrive,
  RefreshCw,
  Search,
  Filter,
  Plus
} from 'lucide-react';

const DownloadsPage = () => {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queries, setQueries] = useState([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exporting, setExporting] = useState(false);

  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getExports();
      setExports(data.exports || []);
    } catch (error) {
      console.error('Failed to fetch exports:', error);
      toast.error('Failed to load exports');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQueries = useCallback(async () => {
    try {
      const history = await getQueryHistory(50);
      // Only show queries with results
      const withResults = history.filter(q => q.results && q.results.length > 0);
      setQueries(withResults);
    } catch (error) {
      console.error('Failed to fetch queries:', error);
    }
  }, []);

  useEffect(() => {
    fetchExports();
    fetchQueries();
  }, [fetchExports, fetchQueries]);

  const handleDelete = async (filename) => {
    if (!window.confirm('Delete this export? This cannot be undone.')) return;
    
    try {
      await deleteExport(filename);
      setExports(prev => prev.filter(e => e.filename !== filename));
      toast.success('Export deleted');
    } catch (error) {
      toast.error('Failed to delete export');
    }
  };

  const handleExport = async () => {
    if (!selectedQuery) return;
    
    setExporting(true);
    try {
      const result = await createExport(
        selectedQuery.id,
        null,
        selectedQuery.query,
        exportFormat
      );
      
      if (result.success) {
        toast.success('Export created! Downloading...');
        // Open download URL
        window.open(result.download_url, '_blank');
        fetchExports();
      } else {
        toast.error(result.error || 'Export failed');
      }
    } catch (error) {
      toast.error('Failed to create export');
    } finally {
      setExporting(false);
      setShowExportDialog(false);
      setSelectedQuery(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileIcon = (filename) => {
    if (filename.endsWith('.xlsx')) return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    if (filename.endsWith('.csv')) return <FileText className="w-5 h-5 text-blue-600" />;
    if (filename.endsWith('.json')) return <FileJson className="w-5 h-5 text-amber-600" />;
    return <FileText className="w-5 h-5 text-gray-600" />;
  };

  return (
    <div className="p-8" data-testid="downloads-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Downloads
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Export query results to Excel, CSV, or JSON - Auto-deleted after 7 days
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={fetchExports}
            className="border-gray-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowExportDialog(true)}
            className="btn-gold"
            data-testid="new-export-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 bg-white border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#D4AF37]/10">
              <Download className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{exports.length}</p>
              <p className="text-sm text-gray-500">Total Exports</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <HardDrive className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {formatFileSize(exports.reduce((acc, e) => acc + (e.size || 0), 0))}
              </p>
              <p className="text-sm text-gray-500">Total Size</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">7 Days</p>
              <p className="text-sm text-gray-500">Auto-Delete Policy</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Exports List */}
      <Card className="bg-white border-gray-200">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Your Exports</h2>
        </div>
        
        <ScrollArea className="h-[500px]">
          {loading ? (
            <div className="p-4 space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : exports.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Download className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="font-medium">No exports yet</p>
              <p className="text-sm mt-1">Export your query results to get started</p>
              <Button 
                onClick={() => setShowExportDialog(true)}
                className="btn-gold mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Export
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {exports.map((exp) => (
                <div 
                  key={exp.key || exp.filename}
                  className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    {getFileIcon(exp.filename)}
                    <div>
                      <p className="font-medium text-gray-900">{exp.filename}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span>{formatFileSize(exp.size || 0)}</span>
                        <span>•</span>
                        <span>{new Date(exp.last_modified).toLocaleDateString()}</span>
                        {exp.row_count && (
                          <>
                            <span>•</span>
                            <span>{exp.row_count} rows</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(exp.download_url, '_blank')}
                      className="border-gray-200"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(exp.filename)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </Card>

      {/* New Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Download className="w-5 h-5 text-[#D4AF37]" />
              Export Query Results
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Select a query and format to export
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Query Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Query</label>
              <Select 
                value={selectedQuery?.id || ''} 
                onValueChange={(id) => setSelectedQuery(queries.find(q => q.id === id))}
              >
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue placeholder="Choose a query with results..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 max-h-60">
                  {queries.map(q => (
                    <SelectItem key={q.id} value={q.id} className="text-gray-700">
                      <div className="flex flex-col">
                        <span className="truncate max-w-[300px]">{q.query}</span>
                        <span className="text-xs text-gray-400">{q.row_count} rows</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Format Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Export Format</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'xlsx', icon: FileSpreadsheet, label: 'Excel', color: 'text-green-600' },
                  { value: 'csv', icon: FileText, label: 'CSV', color: 'text-blue-600' },
                  { value: 'json', icon: FileJson, label: 'JSON', color: 'text-amber-600' }
                ].map(format => (
                  <button
                    key={format.value}
                    onClick={() => setExportFormat(format.value)}
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      exportFormat === format.value 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <format.icon className={`w-6 h-6 ${format.color}`} />
                    <span className="text-sm font-medium text-gray-700">{format.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            {selectedQuery && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Query Preview</p>
                <p className="text-sm text-gray-700 line-clamp-2">{selectedQuery.query}</p>
                <p className="text-xs text-gray-400 mt-1">{selectedQuery.row_count} rows to export</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={!selectedQuery || exporting}
              className="btn-gold"
            >
              {exporting ? 'Exporting...' : 'Export & Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DownloadsPage;
